<script setup lang="ts">
/**
 * The tree pane.
 *
 * Rendered as a FLAT list of `board.visibleRows` with indentation, not as a
 * recursive component. Two reasons, both load-bearing:
 *  - it is the same array the gantt renders, so row n lines up with bar n by
 *    construction and the panes can never drift;
 *  - "infinitely nested" is the headline feature, and a recursive component
 *    tree bottoms out in a stack overflow long before the data model does.
 */
import { nextTick, ref, watch } from "vue";
import { useBoard, type GroupRowData } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import TaskRow from "./TaskRow.vue";
import { formatHours } from "../lib/format.js";

const board = useBoard();
const viewport = useViewport();
const scroller = ref<HTMLElement | null>(null);

// Two-way scroll sync with the gantt, guarded so the two panes do not fight.
let syncing = false;
function onScroll(): void {
  if (syncing || !scroller.value) return;
  syncing = true;
  viewport.scrollTop = scroller.value.scrollTop;
  requestAnimationFrame(() => (syncing = false));
}
watch(
  () => viewport.scrollTop,
  (top) => {
    if (syncing || !scroller.value) return;
    syncing = true;
    scroller.value.scrollTop = top;
    requestAnimationFrame(() => (syncing = false));
  },
);
// The panes take turns between views; one coming back into view picks up where
// the other left off instead of wherever it was when it was hidden.
watch(
  () => viewport.view,
  async () => {
    await nextTick();
    if (scroller.value) scroller.value.scrollTop = viewport.scrollTop;
  },
);

// ── drag and drop ────────────────────────────────────────────────────────
const dragId = ref<string | null>(null);
const overId = ref<string | null>(null);
/** "out" is "into" aimed at the parent you already belong to — see onDragOver. */
const overMode = ref<"into" | "after" | "out">("after");

function onDragStart(id: string, ev: DragEvent): void {
  dragId.value = id;
  ev.dataTransfer?.setData("text/plain", id);
  if (ev.dataTransfer) ev.dataTransfer.effectAllowed = "move";
}

function onDragOver(id: string, ev: DragEvent): void {
  if (!dragId.value || dragId.value === id) return;
  // A draft has no id to hang anything off yet, so it is neither dragged
  // (TaskRow does not offer it) nor dropped into.
  if (board.isDraft(id)) return;
  ev.preventDefault();
  const box = (ev.currentTarget as HTMLElement).getBoundingClientRect();
  // Middle band nests, the lower band reorders. No upper band: "after the row
  // above" already expresses "before this row", so a third target would be a
  // second way to say the same thing.
  // Sorted by urgency, a reorder would write a rank the view then ignores, so
  // the whole row nests instead of offering a drop that visibly does nothing.
  const into = viewport.sortBy === "urgency" || ev.clientY - box.top < box.height * 0.6;
  // Dropping into the parent you are ALREADY in used to be a no-op — the one
  // gesture in the tree that did nothing. It now means "get me out of here":
  // the task leaves the parent and lands directly after it, one level up.
  const nested = board.tasks.get(dragId.value)?.parentId === id;
  overMode.value = into ? (nested ? "out" : "into") : "after";
  overId.value = id;
}

/** A root task already in the group's repo, so it lands at the foot of that
 *  group (omitted afterId = last), and opens straight away to be named. The
 *  trailing row passes null, which files it wherever a repo-less task goes.
 *  An add row on the Personal board makes one only the reader can see.
 *
 *  Nothing is sent by this click: the row is a draft until it is named. */
function addTask(repo: string | null, personal: boolean): void {
  const task = board.addTask({ repo, ...(personal ? { personal } : {}) });
  board.select(task.id);
}

/** The "+" on a repo heading: the same as its add row, but reachable without
 *  scrolling to the foot of a long group, and from a folded one — which opens,
 *  so the new row is not added somewhere nothing on screen shows. */
function addToGroup(row: GroupRowData): void {
  if (!row.expanded) board.toggle(row.key);
  addTask(row.repo ?? null, row.personal ?? false);
}

// ── repo headings ────────────────────────────────────────────────────────
// A heading drag is its own gesture, kept apart from `dragId`: a heading is
// not a task, and nothing a task drop does applies to it. `undefined` = none.
const dragRepo = ref<string | null | undefined>(undefined);
/** Where the dragged heading would land, relative to the one hovered. */
const repoDropSide = ref<"above" | "below">("below");

function onRepoDragStart(repo: string | null, ev: DragEvent): void {
  dragRepo.value = repo;
  ev.dataTransfer?.setData("text/plain", repo ?? "");
  if (ev.dataTransfer) ev.dataTransfer.effectAllowed = "move";
}

/** It takes the hovered heading's slot: after it moving down, before it moving up. */
function onRepoDragOver(key: string, repo: string | null, ev: DragEvent): void {
  if (dragRepo.value === undefined || dragRepo.value === repo) return;
  ev.preventDefault();
  const rows = board.visibleRows;
  const at = (r: string | null): number => rows.findIndex((x) => x.kind === "group" && x.repo === r);
  repoDropSide.value = at(dragRepo.value) < at(repo) ? "below" : "above";
  overId.value = key;
}

function onRepoDrop(repo: string | null): void {
  const moving = dragRepo.value;
  dragRepo.value = undefined;
  overId.value = null;
  if (moving === undefined || moving === repo) return;
  board.moveRepoHeading(moving, repo);
}

function onDragEnd(): void {
  dragId.value = null;
  dragRepo.value = undefined;
  overId.value = null;
}

async function onDrop(id: string): Promise<void> {
  const moving = dragId.value;
  dragId.value = null;
  overId.value = null;
  if (!moving || moving === id) return;

  const target = board.tasks.get(id);
  if (!target) return;

  try {
    if (overMode.value === "into") {
      await board.patchTask(moving, { parentId: id });
    } else {
      // Both "after" and "out" land in the target's own sibling list, directly
      // after it. They differ only in what the gesture meant, and in the cue
      // the row shows while you hover.
      await board.patchTask(moving, { parentId: target.parentId, afterId: id });
    }
  } catch (err) {
    // The server refuses a move that would make a task its own ancestor.
    alert((err as Error).message);
  }
}
</script>

<template>
  <div ref="scroller" class="scroll" @scroll="onScroll" @dragend="onDragEnd">
    <div v-if="!board.loaded" class="skel-list" role="status" aria-label="Loading the board">
      <div v-for="i in 7" :key="i" class="skel-row"><i class="skel skel-dot"></i><i class="skel" :style="{ width: `${30 + ((i * 31) % 50)}%` }"></i><i class="skel skel-count"></i></div>
    </div>
    <template v-for="row in board.loaded ? board.visibleRows : []" :key="row.key">
      <!-- A heading is not a task: it cannot be dragged, dropped on, or
           selected. Clicking it folds the group away. A REPO heading can be
           dragged onto another to reorder them; that order is the reader's own. -->
      <div
        v-if="row.kind === 'group'"
        class="row group-row"
        :class="{
          'dragover-above': row.repo !== undefined && overId === row.key && repoDropSide === 'above',
          'dragover-below': row.repo !== undefined && overId === row.key && repoDropSide === 'below',
        }"
        :style="{ '--depth': row.depth, opacity: row.repo !== undefined && dragRepo === row.repo ? 0.4 : 1 }"
        :draggable="row.repo !== undefined"
        :title="row.repo !== undefined ? 'Drag to reorder repos' : undefined"
        @click="board.toggle(row.key)"
        @dragstart="row.repo !== undefined && onRepoDragStart(row.repo, $event)"
        @dragover="row.repo !== undefined && onRepoDragOver(row.key, row.repo, $event)"
        @dragleave="overId === row.key && (overId = null)"
        @drop.prevent="row.repo !== undefined && onRepoDrop(row.repo)"
      >
        <button class="twisty" :class="{ open: row.expanded }">▶</button>
        <span
          v-if="row.color"
          class="avatar"
          style="width: 18px; height: 18px; font-size: 8px"
          :style="{ background: row.color }"
        >{{ row.label.slice(0, 2) }}</span>
        <span class="group-label">{{ row.label }}</span>
        <span v-if="row.rollup.spentHours > 0" class="spent">
          {{ formatHours(row.rollup.spentHours) }}
        </span>
        <span class="mini"><i :style="{ width: Math.round(row.rollup.percent * 100) + '%' }"></i></span>
        <span class="pct" title="unfinished tasks under this heading">{{ row.count }}</span>
        <button
          v-if="row.repo !== undefined"
          class="add-child"
          type="button"
          :title="row.repo ? `Add a task in ${row.repo}` : 'Add a task with no repo'"
          draggable="false"
          @click.stop="addToGroup(row)"
        >+</button>
      </div>

      <div
        v-else-if="row.kind === 'add'"
        class="row add-row"
        :style="{ '--depth': row.depth }"
        @click="addTask(row.repo, row.personal)"
      >
        <button class="add-task" type="button">+ Add task</button>
      </div>

      <TaskRow
        v-else
        :row="row"
        :dragging="dragId === row.task.id"
        :drop-into="overId === row.task.id && overMode === 'into'"
        :drop-after="overId === row.task.id && overMode === 'after'"
        :drop-out="overId === row.task.id && overMode === 'out'"
        @dragstart="onDragStart(row.task.id, $event)"
        @dragover="onDragOver(row.task.id, $event)"
        @drop="onDrop(row.task.id)"
      />
    </template>
    <!-- There is always an add row now, so "empty" means no TASK rows rather
         than no rows at all. -->
    <p v-if="board.loaded && !board.visibleRows.some((r) => r.kind === 'task')" class="empty">
      Nothing yet — hit “+ Add task”.
    </p>
  </div>
</template>
